import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import api from '../../services/api';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  NoSymbolIcon,
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
  TagIcon,
  XMarkIcon,
  PaperClipIcon,
  ArrowDownTrayIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';
import { toast } from 'react-hot-toast';
import {
  integrationModalField,
  integrationPaginationButton,
  integrationSearchInputBlock,
  integrationSecondaryButton,
  integrationSelect,
  integrationToolbarPrimaryButton,
} from '../../constants/integrationLogsUi';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';
import { uploadAttachmentsDirect } from '../../utils/directAttachmentUpload';

const CASE_SUPPRESSION_RESOURCE = 'case_suppression';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

interface Customer {
  id: string;
  accountName: string;
  accountShortName?: string;
}

interface KeywordUserBrief {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface KeywordAttachmentBrief {
  id: string;
  originalName: string;
  mimeType: string;
  size: number;
  createdAt: string;
  uploadedBy?: KeywordUserBrief;
  downloadUrl?: string;
}

interface Keyword {
  id: string;
  customerId: string;
  keyword: string;
  matchType: 'EXACT' | 'CONTAINS';
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
  updatedAt: string;
  customerName?: string | null; // Top-level from API for reliable display
  /** Rows in suppression logs matching this keyword (customer + text + match type) */
  suppressedAlertCount?: number;
  Customer?: Customer | null;
  customer?: Customer | null; // camelCase fallback if API serializes differently
  createdBy?: KeywordUserBrief | null;
  updatedBy?: KeywordUserBrief | null;
  attachmentCount?: number;
}

interface SuppressedLog {
  id: string;
  alertId: string;
  customerId: string | null;
  matchedKeyword: string;
  matchType: string;
  reason: string;
  createdAt: string;
  Alert: {
    id: string;
    number: string;
    shortDescription: string;
    state: string;
  };
  Customer?: Customer | null;
}

const MATCH_TYPES = [
  { value: 'EXACT', label: 'Exact Match (Whole Word)' },
  { value: 'CONTAINS', label: 'Contains' },
];

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const MATCH_TYPE_FILTER_OPTIONS = [
  { value: 'EXACT', label: 'Exact Match' },
  { value: 'CONTAINS', label: 'Contains' },
];

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

function customerDisplayName(c: Customer): string {
  return c.accountName || c.accountShortName || c.id;
}

function userDisplayName(u: KeywordUserBrief): string {
  const n = `${u.firstName || ''} ${u.lastName || ''}`.trim();
  return n || u.email || u.id;
}

const PROOF_ACCEPT =
  '.eml,.msg,.png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.txt';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function matchTypeLabel(value: string): string {
  return MATCH_TYPES.find((m) => m.value === value)?.label ?? value;
}

async function downloadKeywordAttachment(keywordId: string, att: KeywordAttachmentBrief) {
  const url = att.downloadUrl;
  if (url?.startsWith('https://')) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  const path = url || `/case-suppression/keywords/${keywordId}/attachments/${att.id}/download`;
  const res = await api.get(path, { responseType: 'blob' });
  const href = window.URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = att.originalName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(href);
}

const PREVIEW_MAX_TEXT_BYTES = 512 * 1024;
const PREVIEW_MAX_TEXT_CHARS = 120_000;

type ProofPreviewKind = 'image' | 'pdf' | 'text' | 'none';

function proofPreviewKind(att: KeywordAttachmentBrief): ProofPreviewKind {
  const name = att.originalName || '';
  const mime = (att.mimeType || '').toLowerCase();
  if (/\.(png|jpe?g|gif|webp)$/i.test(name) || mime.startsWith('image/')) return 'image';
  if (/\.pdf$/i.test(name) || mime.includes('pdf')) return 'pdf';
  if (/\.(txt|eml)$/i.test(name) || mime === 'text/plain' || mime === 'message/rfc822' || mime.includes('text/'))
    return 'text';
  return 'none';
}

/** Inline preview in view modal: images, PDF (iframe), plain text / .eml */
function KeywordAttachmentPreview({ keywordId, att }: { keywordId: string; att: KeywordAttachmentBrief }) {
  const kind = proofPreviewKind(att);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(kind !== 'none');

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    async function load() {
      setLoading(true);
      setError(null);
      setPreviewUrl(null);
      setTextContent(null);

      if (kind === 'none') {
        setLoading(false);
        return;
      }

      const sasUrl = att.downloadUrl?.startsWith('https://') ? att.downloadUrl : null;

      try {
        if (kind === 'image' || kind === 'pdf') {
          if (sasUrl) {
            if (!cancelled) setPreviewUrl(sasUrl);
          } else {
            const path =
              att.downloadUrl || `/case-suppression/keywords/${keywordId}/attachments/${att.id}/download`;
            const res = await api.get(path, { responseType: 'blob' });
            const blob = res.data as Blob;
            objectUrl = URL.createObjectURL(blob);
            if (!cancelled) setPreviewUrl(objectUrl);
          }
        } else if (kind === 'text') {
          let blob: Blob;
          if (sasUrl) {
            const r = await fetch(sasUrl);
            if (!r.ok) throw new Error('fetch failed');
            blob = await r.blob();
          } else {
            const path =
              att.downloadUrl || `/case-suppression/keywords/${keywordId}/attachments/${att.id}/download`;
            const res = await api.get(path, { responseType: 'blob' });
            blob = res.data as Blob;
          }
          if (blob.size > PREVIEW_MAX_TEXT_BYTES) {
            if (!cancelled) setError('File is too large to preview here. Use download to open it.');
            return;
          }
          const text = await blob.text();
          if (cancelled) return;
          const out =
            text.length > PREVIEW_MAX_TEXT_CHARS
              ? `${text.slice(0, PREVIEW_MAX_TEXT_CHARS)}\n\n… (truncated — download for full file)`
              : text;
          setTextContent(out);
        }
      } catch {
        if (!cancelled) setError('Preview could not be loaded. Try download instead.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [keywordId, att.id, att.downloadUrl, att.originalName, att.mimeType, kind]);

  if (kind === 'none') {
    return (
      <p className="mt-2 text-[11px] text-gray-500 border border-dashed border-gray-200 rounded-md px-2 py-2 bg-white/80">
        No inline preview for this type (.msg, Word, etc.). Use download to open the file.
      </p>
    );
  }

  if (loading) {
    return <p className="mt-2 text-[11px] text-gray-400">Loading preview…</p>;
  }

  if (error) {
    return <p className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5">{error}</p>;
  }

  if (kind === 'image' && previewUrl) {
    return (
      <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 overflow-hidden">
        <img
          src={previewUrl}
          alt={`Preview of ${att.originalName}`}
          className="max-h-72 w-full object-contain"
        />
      </div>
    );
  }

  if (kind === 'pdf' && previewUrl) {
    return (
      <div className="mt-2 space-y-1">
        <div className="rounded-md border border-gray-200 overflow-hidden bg-gray-100">
          <iframe title={`PDF preview: ${att.originalName}`} src={previewUrl} className="w-full h-80 border-0" />
        </div>
        <a
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-indigo-600 hover:text-indigo-800"
        >
          Open PDF in new tab
        </a>
      </div>
    );
  }

  if (kind === 'text' && textContent !== null) {
    return (
      <pre
        className="mt-2 max-h-64 overflow-auto rounded-md border border-gray-200 bg-white p-2 text-[11px] leading-relaxed text-gray-800 whitespace-pre-wrap break-words font-mono"
        tabIndex={0}
      >
        {textContent}
      </pre>
    );
  }

  return null;
}

/** One attachment in view modal: metadata, download, and preview only after "View preview". */
function ViewModalAttachmentRow({ keywordId, att }: { keywordId: string; att: KeywordAttachmentBrief }) {
  const [previewOpen, setPreviewOpen] = useState(false);

  return (
    <li className="rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-gray-900 truncate" title={att.originalName}>
            {att.originalName}
          </div>
          <div className="text-gray-500 mt-0.5">
            {formatFileSize(att.size)} · {att.mimeType}
            {att.uploadedBy && (
              <>
                {' · '}
                {userDisplayName(att.uploadedBy)}
              </>
            )}
            {' · '}
            {new Date(att.createdAt).toLocaleString()}
          </div>
        </div>
        <div className="flex flex-row items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setPreviewOpen((o) => !o)}
            className={`text-xs font-medium px-2.5 py-1.5 rounded-md border ${
              previewOpen
                ? 'border-gray-300 bg-gray-100 text-gray-800 hover:bg-gray-200'
                : 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
            }`}
          >
            {previewOpen ? 'Hide preview' : 'View preview'}
          </button>
          <button
            type="button"
            onClick={() => downloadKeywordAttachment(keywordId, att)}
            className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-indigo-700 border border-indigo-200 rounded-md hover:bg-indigo-50"
            title="Download file"
            aria-label={`Download ${att.originalName}`}
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Download
          </button>
        </div>
      </div>
      {previewOpen && (
        <div className="mt-3 pt-2 border-t border-gray-200/80">
          <KeywordAttachmentPreview keywordId={keywordId} att={att} />
        </div>
      )}
    </li>
  );
}

/** Resets search when popover closes */
function ResetQueryWhenClosed({ open, onReset }: { open: boolean; onReset: () => void }) {
  useEffect(() => {
    if (!open) onReset();
  }, [open, onReset]);
  return null;
}

/** Customer filter: trigger shows selection; search field is inside the dropdown panel */
function CustomerFilterComboboxInner({
  open,
  close,
  customers,
  value,
  onChange,
  inputId,
}: {
  open: boolean;
  close: () => void;
  customers: Customer[];
  value: string;
  onChange: (customerId: string) => void;
  inputId?: string;
}) {
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  const options = useMemo(() => {
    const all = { id: '', label: 'All Customers' };
    return [all, ...customers.map((c) => ({ id: c.id, label: customerDisplayName(c) }))];
  }, [customers]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const selectedLabel = options.find((x) => x.id === value)?.label ?? 'All Customers';
  const resetQuery = useCallback(() => setQuery(''), []);
  const hasSelection = Boolean(value);

  return (
    <>
      <ResetQueryWhenClosed open={open} onReset={resetQuery} />
      <div className="relative w-full">
        <PopoverButton
          type="button"
          id={inputId}
          className={`${integrationSelect} relative flex w-full items-center text-left ${
            hasSelection ? 'pr-16' : 'pr-9'
          }`}
        >
          <span className="min-w-0 flex-1 truncate">{selectedLabel}</span>
          <ChevronUpDownIcon
            className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 shrink-0 text-gray-400"
            aria-hidden
          />
        </PopoverButton>
        {hasSelection && (
          <button
            type="button"
            aria-label="Clear customer filter — show all customers"
            title="Clear customer filter"
            className="absolute right-8 top-1/2 z-10 -translate-y-1/2 rounded p-0.5 text-red-500 transition-colors hover:text-red-700 focus:outline-none"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange('');
              resetQuery();
            }}
          >
            <XMarkIcon className="h-4 w-4 text-current" aria-hidden />
          </button>
        )}
      </div>
      <PopoverPanel
        transition
        anchor="bottom start"
        className="z-40 mt-1 max-h-[min(22rem,calc(100vh-8rem))] w-[var(--button-width)] overflow-hidden rounded-md bg-white text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
      >
        <div className="border-b border-gray-200 bg-white p-2">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              ref={searchInputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search customers…"
              className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-2 text-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              autoComplete="off"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        <ul className="max-h-52 overflow-y-auto py-1" role="listbox">
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-gray-500">No customers match.</li>
          ) : (
            filtered.map((option) => {
              const selected = value === option.id;
              return (
                <li key={option.id === '' ? '__all__' : option.id} role="none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left ${
                      selected ? 'bg-indigo-50 text-indigo-900' : 'text-gray-900 hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      onChange(option.id);
                      resetQuery();
                      close();
                    }}
                  >
                    <span className="flex w-5 shrink-0 justify-center" aria-hidden>
                      {selected ? <CheckIcon className="h-4 w-4 text-indigo-600" /> : null}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </PopoverPanel>
    </>
  );
}

function CustomerFilterCombobox({
  customers,
  value,
  onChange,
  className = '',
  inputId,
}: {
  customers: Customer[];
  value: string;
  onChange: (customerId: string) => void;
  className?: string;
  inputId?: string;
}) {
  return (
    <div className={`relative w-full min-w-[200px] max-w-[280px] ${className}`}>
      <Popover className="relative w-full">
        {({ open, close }) => (
          <CustomerFilterComboboxInner
            open={open}
            close={close}
            customers={customers}
            value={value}
            onChange={onChange}
            inputId={inputId}
          />
        )}
      </Popover>
    </div>
  );
}

function CustomerSelectComboboxInner({
  open,
  close,
  customers,
  value,
  onChange,
  required,
  inputId,
}: {
  open: boolean;
  close: () => void;
  customers: Customer[];
  value: string;
  onChange: (customerId: string) => void;
  required?: boolean;
  inputId?: string;
}) {
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  const options = useMemo(
    () => customers.map((c) => ({ id: c.id, label: customerDisplayName(c) })),
    [customers]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const selectedLabel = options.find((x) => x.id === value)?.label ?? '';
  const resetQuery = useCallback(() => setQuery(''), []);

  return (
    <>
      <ResetQueryWhenClosed open={open} onReset={resetQuery} />
      <PopoverButton
        type="button"
        id={inputId}
        aria-required={required}
        className={`${integrationSelect} flex w-full items-center justify-between gap-2 text-left`}
      >
        <span className="min-w-0 flex-1 truncate">
          {selectedLabel || (required ? 'Select customer…' : 'Optional')}
        </span>
        <ChevronUpDownIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
      </PopoverButton>
      <PopoverPanel
        transition
        anchor="bottom start"
        className="z-50 mt-1 max-h-[min(22rem,calc(100vh-8rem))] w-[var(--button-width)] overflow-hidden rounded-md bg-white text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
      >
        <div className="border-b border-gray-200 bg-white p-2">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              ref={searchInputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search customers…"
              className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-2 text-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              autoComplete="off"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        <ul className="max-h-52 overflow-y-auto py-1" role="listbox">
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-gray-500">No customers match.</li>
          ) : (
            filtered.map((option) => {
              const selected = value === option.id;
              return (
                <li key={option.id} role="none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left ${
                      selected ? 'bg-indigo-50 text-indigo-900' : 'text-gray-900 hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      onChange(option.id);
                      resetQuery();
                      close();
                    }}
                  >
                    <span className="flex w-5 shrink-0 justify-center" aria-hidden>
                      {selected ? <CheckIcon className="h-4 w-4 text-indigo-600" /> : null}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </PopoverPanel>
    </>
  );
}

/** Customer picker for forms (no “All” row); search is inside the dropdown */
function CustomerSelectCombobox({
  customers,
  value,
  onChange,
  className = '',
  required,
  inputId,
}: {
  customers: Customer[];
  value: string;
  onChange: (customerId: string) => void;
  className?: string;
  required?: boolean;
  inputId?: string;
}) {
  return (
    <Popover className={`relative mt-1 block w-full ${className}`}>
      {({ open, close }) => (
        <CustomerSelectComboboxInner
          open={open}
          close={close}
          customers={customers}
          value={value}
          onChange={onChange}
          required={required}
          inputId={inputId}
        />
      )}
    </Popover>
  );
}

const CaseSuppressionKeywords: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreateEntity, canUpdateEntity, canDeleteEntity, canViewEntity } = useUserPermissions();
  const canAddKeyword = canCreateEntity(CASE_SUPPRESSION_RESOURCE);
  const canViewKeyword = canViewEntity(CASE_SUPPRESSION_RESOURCE);
  const canEditKeyword = canUpdateEntity(CASE_SUPPRESSION_RESOURCE);
  const canRemoveKeyword = canDeleteEntity(CASE_SUPPRESSION_RESOURCE);
  const showKeywordRowActions = canViewKeyword || canEditKeyword || canRemoveKeyword;

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const activeTab = (searchParams.get('tab') === 'logs' ? 'logs' : 'keywords') as 'keywords' | 'logs';

  const setActiveTab = (tab: 'keywords' | 'logs') => {
    patchSearchParams({ tab: tab === 'keywords' ? null : tab });
  };

  const keywordPage = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const keywordLimit = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '20', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 20;
  }, [searchParams]);

  const logPage = useMemo(() => {
    const p = parseInt(searchParams.get('logPage') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const logLimit = useMemo(() => {
    const ps = parseInt(searchParams.get('logPageSize') || '20', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 20;
  }, [searchParams]);

  const appliedKeywordSearch = searchParams.get('search') ?? '';
  const keywordFilterCustomer = searchParams.get('customerId') ?? '';
  const filterStatus = searchParams.get('status') ?? '';
  const filterKeyword = searchParams.get('keyword') ?? '';
  const filterMatchType = searchParams.get('matchType') ?? '';

  const appliedLogsSearch = searchParams.get('logSearch') ?? '';
  const logFilterCustomer = searchParams.get('logCustomerId') ?? '';
  const filterLogAlert = searchParams.get('logAlert') ?? '';
  const filterLogMatchedKeyword = searchParams.get('logMatchedKeyword') ?? '';
  const filterLogMatchType = searchParams.get('logMatchType') ?? '';
  const logsMatchedKeywordExact = searchParams.get('matchedKeywordExact') ?? '';

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const [logsSearchInput, setLogsSearchInput] = useState(() => searchParams.get('logSearch') ?? '');

  useEffect(() => {
    setSearchInput(appliedKeywordSearch);
  }, [appliedKeywordSearch]);

  useEffect(() => {
    setLogsSearchInput(appliedLogsSearch);
  }, [appliedLogsSearch]);

  useEffect(() => {
    const id = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed === appliedKeywordSearch) return;
      patchSearchParams({ search: trimmed || null, page: '1' });
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput, appliedKeywordSearch, patchSearchParams]);

  useEffect(() => {
    const id = setTimeout(() => {
      const trimmed = logsSearchInput.trim();
      if (trimmed === appliedLogsSearch) return;
      const updates: Record<string, string | null> = { logSearch: trimmed || null, logPage: '1' };
      if (logsMatchedKeywordExact && trimmed !== logsMatchedKeywordExact.trim()) {
        updates.matchedKeywordExact = null;
      }
      patchSearchParams(updates);
    }, 300);
    return () => clearTimeout(id);
  }, [logsSearchInput, appliedLogsSearch, logsMatchedKeywordExact, patchSearchParams]);

  // Add/Edit Modal
  const [showModal, setShowModal] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState<Keyword | null>(null);
  const [formData, setFormData] = useState({
    customerId: '',
    keyword: '',
    matchType: 'CONTAINS' as 'EXACT' | 'CONTAINS',
    status: 'ACTIVE' as 'ACTIVE' | 'INACTIVE',
  });
  const [saving, setSaving] = useState(false);
  const [pendingProofFiles, setPendingProofFiles] = useState<File[]>([]);

  // Read-only details (view keyword + attachments)
  const [viewingKeyword, setViewingKeyword] = useState<Keyword | null>(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<Keyword | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch customers for dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers-list'],
    queryFn: async () => {
      const res = await api.get('/customers?limit=1000');
      return res.data;
    },
    staleTime: 60000,
  });
  const customers: Customer[] = (() => {
    const raw = customersData?.customers ?? customersData?.data ?? customersData;
    return Array.isArray(raw) ? raw : [];
  })();

  const customerOptions = useMemo(
    () => customers.map((c) => ({ value: c.id, label: customerDisplayName(c) })),
    [customers]
  );

  // Fetch keywords (paginated + server search)
  const { data: keywordsData, isLoading: keywordsLoading } = useQuery({
    queryKey: [
      'case-suppression-keywords',
      keywordFilterCustomer,
      filterStatus,
      filterKeyword,
      filterMatchType,
      appliedKeywordSearch,
      keywordPage,
      keywordLimit,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: keywordPage.toString(),
        limit: keywordLimit.toString(),
      });
      if (keywordFilterCustomer) params.set('customerId', keywordFilterCustomer);
      if (filterStatus) params.set('status', filterStatus);
      if (filterKeyword) params.set('keyword', filterKeyword);
      if (filterMatchType) params.set('matchType', filterMatchType);
      if (appliedKeywordSearch) params.set('search', appliedKeywordSearch);
      const res = await api.get(`/case-suppression/keywords?${params}`);
      return res.data;
    },
    enabled: activeTab === 'keywords',
  });
  const keywordsPayload = keywordsData?.data;
  const keywords: Keyword[] = keywordsPayload?.keywords ?? [];
  const keywordsTotal = keywordsPayload?.total ?? 0;
  const keywordsTotalPages = keywordsPayload?.totalPages ?? 0;

  const keywordDetailId =
    (showModal && editingKeyword?.id) || viewingKeyword?.id || null;
  const { data: keywordDetailData, isLoading: keywordDetailLoading } = useQuery({
    queryKey: ['case-suppression-keyword', keywordDetailId],
    queryFn: async () => {
      const res = await api.get(`/case-suppression/keywords/${keywordDetailId}`);
      const raw = res.data;
      const payload = raw?.data ?? raw;
      const attachments = payload?.attachments;
      return {
        ...payload,
        attachments: Array.isArray(attachments) ? attachments : [],
      } as Keyword & { attachments: KeywordAttachmentBrief[] };
    },
    enabled: !!keywordDetailId,
    refetchOnMount: 'always',
  });
  const detailAttachments: KeywordAttachmentBrief[] = keywordDetailData?.attachments ?? [];
  const viewDetailMerged: Keyword | null =
    viewingKeyword &&
    keywordDetailData?.id === viewingKeyword.id &&
    keywordDetailData
      ? { ...viewingKeyword, ...keywordDetailData }
      : viewingKeyword;

  // Fetch suppression logs (paginated + server search)
  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: [
      'case-suppression-logs',
      logPage,
      logLimit,
      logFilterCustomer,
      appliedLogsSearch,
      filterLogAlert,
      filterLogMatchedKeyword,
      filterLogMatchType,
      logsMatchedKeywordExact,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: logPage.toString(),
        limit: logLimit.toString(),
      });
      if (logFilterCustomer) params.set('customerId', logFilterCustomer);
      const exact = logsMatchedKeywordExact?.trim();
      if (exact) {
        params.set('matchedKeywordExact', exact);
      } else {
        if (filterLogMatchedKeyword) {
          params.set('matchedKeyword', filterLogMatchedKeyword);
        } else if (appliedLogsSearch) {
          params.set('search', appliedLogsSearch);
        }
      }
      if (filterLogAlert) params.set('alert', filterLogAlert);
      if (filterLogMatchType) params.set('matchType', filterLogMatchType);
      const res = await api.get(`/case-suppression/logs?${params}`);
      return res.data;
    },
    enabled: activeTab === 'logs',
  });
  const logsResult = logsData?.data || { logs: [], total: 0, page: 1, limit: 20, totalPages: 0 };
  const logs: SuppressedLog[] = logsResult.logs || [];
  const logsTotal = logsResult.total || 0;
  const logsTotalPages = logsResult.totalPages || 0;

  const openViewModal = (kw: Keyword) => {
    setShowModal(false);
    setEditingKeyword(null);
    setPendingProofFiles([]);
    setViewingKeyword(kw);
  };

  const closeViewModal = () => {
    setViewingKeyword(null);
  };

  const openAddModal = () => {
    setViewingKeyword(null);
    setEditingKeyword(null);
    setPendingProofFiles([]);
    setFormData({
      customerId: keywordFilterCustomer || '',
      keyword: '',
      matchType: 'CONTAINS',
      status: 'ACTIVE',
    });
    setShowModal(true);
  };

  const openEditModal = (kw: Keyword) => {
    setViewingKeyword(null);
    setEditingKeyword(kw);
    setPendingProofFiles([]);
    setFormData({
      customerId: kw.customerId,
      keyword: kw.keyword,
      matchType: kw.matchType,
      status: kw.status,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingKeyword(null);
    setPendingProofFiles([]);
  };

  const saveKeyword = useMutation({
    mutationFn: async () => {
      if (editingKeyword) {
        return api.patch(`/case-suppression/keywords/${editingKeyword.id}`, {
          keyword: formData.keyword,
          matchType: formData.matchType,
          status: formData.status,
        });
      } else {
        return api.post('/case-suppression/keywords', formData);
      }
    },
    onSuccess: async (res) => {
      const savedId = res?.data?.data?.id ?? editingKeyword?.id;
      const proofCount = pendingProofFiles.length;
      if (savedId && proofCount > 0) {
        try {
          await uploadAttachmentsDirect(
            api,
            `/case-suppression/keywords/${savedId}/attachments`,
            pendingProofFiles,
            { wrappedResponse: true, multipartFieldName: 'file' }
          );
        } catch (e: any) {
          toast.error(e.response?.data?.message || 'Keyword saved but proof upload failed');
          queryClient.invalidateQueries({ queryKey: ['case-suppression-keywords'] });
          if (savedId) queryClient.invalidateQueries({ queryKey: ['case-suppression-keyword', savedId] });
          closeModal();
          return;
        }
      }
      queryClient.invalidateQueries({ queryKey: ['case-suppression-keywords'] });
      if (savedId) queryClient.invalidateQueries({ queryKey: ['case-suppression-keyword', savedId] });
      if (proofCount > 0) {
        toast.success(
          editingKeyword
            ? `Keyword updated and ${proofCount} proof file(s) uploaded`
            : `Keyword added and ${proofCount} proof file(s) uploaded`
        );
      } else {
        toast.success(editingKeyword ? 'Keyword updated' : 'Keyword added');
      }
      closeModal();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to save keyword');
    },
  });

  const deleteAttachment = useMutation({
    mutationFn: async ({ keywordId, attachmentId }: { keywordId: string; attachmentId: string }) =>
      api.delete(`/case-suppression/keywords/${keywordId}/attachments/${attachmentId}`),
    onSuccess: (_, { keywordId }) => {
      queryClient.invalidateQueries({ queryKey: ['case-suppression-keyword', keywordId] });
      queryClient.invalidateQueries({ queryKey: ['case-suppression-keywords'] });
      toast.success('Attachment removed');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to remove attachment');
    },
  });

  const keywordModalDetail =
    showModal && editingKeyword ? keywordDetailData ?? editingKeyword : null;
  const keywordModalUpdatedDiffers =
    keywordModalDetail &&
    keywordModalDetail.updatedBy &&
    new Date(keywordModalDetail.updatedAt).getTime() !==
      new Date(keywordModalDetail.createdAt).getTime();

  const deleteKeyword = useMutation({
    mutationFn: async (id: string) => api.delete(`/case-suppression/keywords/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-suppression-keywords'] });
      toast.success('Keyword deleted');
      setDeleteTarget(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to delete keyword');
    },
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingKeyword && !canEditKeyword) {
      toast.error('You do not have permission to update keywords.');
      return;
    }
    if (!editingKeyword && !canAddKeyword) {
      toast.error('You do not have permission to add keywords.');
      return;
    }
    if (!formData.keyword.trim()) {
      toast.error('Keyword is required');
      return;
    }
    if (!editingKeyword && !formData.customerId) {
      toast.error('Customer is required');
      return;
    }
    setSaving(true);
    saveKeyword.mutate(undefined, {
      onSettled: () => setSaving(false),
    });
  };

  const handleDelete = (kw: Keyword) => {
    if (!canRemoveKeyword) {
      toast.error('You do not have permission to delete keywords.');
      return;
    }
    setDeleting(true);
    deleteKeyword.mutate(kw.id, {
      onSettled: () => setDeleting(false),
    });
  };

  const openLogsForKeyword = (kw: Keyword) => {
    patchSearchParams({
      tab: 'logs',
      logCustomerId: kw.customerId,
      logSearch: kw.keyword,
      matchedKeywordExact: kw.keyword,
      logPage: '1',
    });
    setLogsSearchInput(kw.keyword);
  };

  const renderKeywordColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'customer':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={customerOptions}
              value={keywordFilterCustomer}
              placeholder="All Customers"
              onChange={(v) => {
                patchSearchParams({ customerId: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'keyword':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterKeyword}
              placeholder="Filter keyword..."
              onChange={(v) => {
                patchSearchParams({ keyword: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'matchType':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={MATCH_TYPE_FILTER_OPTIONS}
              value={filterMatchType}
              placeholder="All Types"
              onChange={(v) => {
                patchSearchParams({ matchType: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'status':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={STATUS_OPTIONS}
              value={filterStatus}
              placeholder="All Statuses"
              onChange={(v) => {
                patchSearchParams({ status: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [customerOptions, keywordFilterCustomer, filterKeyword, filterMatchType, filterStatus, patchSearchParams]
  );

  const renderLogColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'alert':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterLogAlert}
              placeholder="Filter alert #..."
              onChange={(v) => {
                patchSearchParams({ logAlert: typeof v === 'string' && v ? v : null, logPage: '1' });
              }}
            />
          );
        case 'customer':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={customerOptions}
              value={logFilterCustomer}
              placeholder="All Customers"
              onChange={(v) => {
                patchSearchParams({ logCustomerId: typeof v === 'string' && v ? v : null, logPage: '1' });
              }}
            />
          );
        case 'matchedKeyword':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterLogMatchedKeyword || logsMatchedKeywordExact}
              placeholder="Filter keyword..."
              onChange={(v) => {
                patchSearchParams({
                  logMatchedKeyword: typeof v === 'string' && v ? v : null,
                  matchedKeywordExact: null,
                  logPage: '1',
                });
              }}
            />
          );
        case 'matchType':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={MATCH_TYPE_FILTER_OPTIONS}
              value={filterLogMatchType}
              placeholder="All Types"
              onChange={(v) => {
                patchSearchParams({ logMatchType: typeof v === 'string' && v ? v : null, logPage: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [
      customerOptions,
      filterLogAlert,
      logFilterCustomer,
      filterLogMatchedKeyword,
      logsMatchedKeywordExact,
      filterLogMatchType,
      patchSearchParams,
    ]
  );

  const keywordFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (appliedKeywordSearch) {
      chips.push({ id: 'search', label: 'Search', value: appliedKeywordSearch, color: 'gray' });
    }
    if (keywordFilterCustomer) {
      const c = customerOptions.find((o) => o.value === keywordFilterCustomer);
      chips.push({ id: 'customerId', label: 'Customer', value: c?.label || 'Unknown', color: 'purple' });
    }
    if (filterKeyword) {
      chips.push({ id: 'keyword', label: 'Keyword', value: filterKeyword, color: 'blue' });
    }
    if (filterMatchType) {
      const label = MATCH_TYPE_FILTER_OPTIONS.find((o) => o.value === filterMatchType)?.label || filterMatchType;
      chips.push({ id: 'matchType', label: 'Match Type', value: label, color: 'teal' });
    }
    if (filterStatus) {
      const label = STATUS_OPTIONS.find((o) => o.value === filterStatus)?.label || filterStatus;
      chips.push({ id: 'status', label: 'Status', value: label, color: 'green' });
    }
    return chips;
  }, [appliedKeywordSearch, keywordFilterCustomer, filterKeyword, filterMatchType, filterStatus, customerOptions]);

  const logFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (appliedLogsSearch) {
      chips.push({ id: 'logSearch', label: 'Search', value: appliedLogsSearch, color: 'gray' });
    }
    if (logFilterCustomer) {
      const c = customerOptions.find((o) => o.value === logFilterCustomer);
      chips.push({ id: 'logCustomerId', label: 'Customer', value: c?.label || 'Unknown', color: 'purple' });
    }
    if (filterLogAlert) {
      chips.push({ id: 'logAlert', label: 'Alert', value: filterLogAlert, color: 'blue' });
    }
    if (filterLogMatchedKeyword || logsMatchedKeywordExact) {
      chips.push({
        id: 'logMatchedKeyword',
        label: 'Matched Keyword',
        value: filterLogMatchedKeyword || logsMatchedKeywordExact,
        color: 'blue',
      });
    }
    if (filterLogMatchType) {
      const label = MATCH_TYPE_FILTER_OPTIONS.find((o) => o.value === filterLogMatchType)?.label || filterLogMatchType;
      chips.push({ id: 'logMatchType', label: 'Match Type', value: label, color: 'teal' });
    }
    return chips;
  }, [
    appliedLogsSearch,
    logFilterCustomer,
    filterLogAlert,
    filterLogMatchedKeyword,
    logsMatchedKeywordExact,
    filterLogMatchType,
    customerOptions,
  ]);

  const handleRemoveKeywordFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'customerId':
        patchSearchParams({ customerId: null, page: '1' });
        break;
      case 'keyword':
        patchSearchParams({ keyword: null, page: '1' });
        break;
      case 'matchType':
        patchSearchParams({ matchType: null, page: '1' });
        break;
      case 'status':
        patchSearchParams({ status: null, page: '1' });
        break;
    }
  };

  const handleRemoveLogFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'logSearch':
        setLogsSearchInput('');
        patchSearchParams({ logSearch: null, matchedKeywordExact: null, logPage: '1' });
        break;
      case 'logCustomerId':
        patchSearchParams({ logCustomerId: null, logPage: '1' });
        break;
      case 'logAlert':
        patchSearchParams({ logAlert: null, logPage: '1' });
        break;
      case 'logMatchedKeyword':
        patchSearchParams({ logMatchedKeyword: null, matchedKeywordExact: null, logPage: '1' });
        break;
      case 'logMatchType':
        patchSearchParams({ logMatchType: null, logPage: '1' });
        break;
    }
  };

  const clearKeywordFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      customerId: null,
      keyword: null,
      matchType: null,
      status: null,
      page: '1',
    });
  };

  const clearLogFilters = () => {
    setLogsSearchInput('');
    patchSearchParams({
      logSearch: null,
      matchedKeywordExact: null,
      logCustomerId: null,
      logAlert: null,
      logMatchedKeyword: null,
      logMatchType: null,
      logDateFrom: null,
      logDateTo: null,
      logPage: '1',
    });
  };

  const openLogsTabFresh = () => {
    clearLogFilters();
    setActiveTab('logs');
  };

  return (
    <div className="p-2 sm:p-3 flex flex-col h-full min-h-0 overflow-hidden">
      <div className="bg-white shadow rounded-md mb-2 flex-shrink-0">
        <div className="px-3 py-2">
          <h1 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">Case Suppression</h1>
          <p className="text-xs text-gray-500 mt-0.5 leading-snug line-clamp-2">
            Suppress Alert → Case creation when keywords match; events are logged.
          </p>
        </div>
      </div>

      {/* Primary section tabs — compact segmented control */}
      <div className="flex-shrink-0 mb-2">
        <nav
          className="flex rounded-lg border border-slate-200 bg-gradient-to-b from-slate-50 to-slate-100/90 p-1 shadow-sm ring-1 ring-slate-200/60"
          role="tablist"
          aria-label="Case suppression views"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'keywords'}
            id="tab-keywords"
            title="Rules and match types"
            onClick={() => setActiveTab('keywords')}
            className={`relative flex flex-1 flex-row items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-center text-sm font-semibold transition-all sm:gap-2 sm:px-4 ${
              activeTab === 'keywords'
                ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200/90'
                : 'text-slate-600 hover:bg-white/60 hover:text-slate-900'
            }`}
          >
            <TagIcon
              className={`h-4 w-4 shrink-0 ${activeTab === 'keywords' ? 'text-indigo-600' : 'text-slate-400'}`}
              aria-hidden
            />
            <span>Keywords</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'logs'}
            id="tab-logs"
            title="Alerts blocked from case creation"
            onClick={openLogsTabFresh}
            className={`relative flex flex-1 flex-row items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-center text-sm font-semibold transition-all sm:gap-2 sm:px-4 ${
              activeTab === 'logs'
                ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200/90'
                : 'text-slate-600 hover:bg-white/60 hover:text-slate-900'
            }`}
          >
            <ClipboardDocumentListIcon
              className={`h-4 w-4 shrink-0 ${activeTab === 'logs' ? 'text-indigo-600' : 'text-slate-400'}`}
              aria-hidden
            />
            <span className="whitespace-nowrap">Suppression logs</span>
          </button>
        </nav>
      </div>

      {activeTab === 'keywords' && (
        <div
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          role="tabpanel"
          id="panel-keywords"
          aria-labelledby="tab-keywords"
        >
          <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
            <div className="px-3 py-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="relative flex-1 min-w-[200px] max-w-xs">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search keywords, customer..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className={`${integrationSearchInputBlock} !py-1.5 !pr-9 text-sm`}
                  />
                </div>
                {canAddKeyword && (
                  <button
                    type="button"
                    onClick={openAddModal}
                    className={`${integrationToolbarPrimaryButton} !py-1.5 text-sm shrink-0`}
                  >
                    <PlusIcon className="h-4 w-4 mr-1.5" />
                    Add Keyword
                  </button>
                )}
              </div>
              {keywordFilterChips.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <ActiveFilterChips
                    chips={keywordFilterChips}
                    onRemove={handleRemoveKeywordFilterChip}
                    onClearAll={clearKeywordFilters}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="bg-white shadow rounded-lg flex flex-col flex-1 min-h-0 overflow-hidden">
            {keywordsLoading ? (
              <div className="flex-1 min-h-0 overflow-auto p-4 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/4 mb-3" />
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <div key={i} className="h-8 bg-gray-200 rounded" />
                  ))}
                </div>
              </div>
            ) : keywords.length === 0 ? (
              <div className="flex-1 min-h-0 overflow-auto p-6 text-center text-gray-500 text-sm">
                <NoSymbolIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <p>{appliedKeywordSearch || keywordFilterChips.length > 0 ? 'No keywords match your search.' : 'No keywords configured. Add keywords to suppress automatic case creation for matching alerts.'}</p>
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-auto">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">
                          {renderKeywordColumnHeader('customer', 'Customer')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">
                          {renderKeywordColumnHeader('keyword', 'Keyword')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">
                          {renderKeywordColumnHeader('matchType', 'Match Type')}
                        </th>
                        <th
                          className="px-1.5 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tabular-nums whitespace-nowrap"
                          title="Suppressed"
                          scope="col"
                        >
                          Supp...
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">
                          {renderKeywordColumnHeader('status', 'Status')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Added</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
                        <th className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase w-20">
                          Proof
                        </th>
                        {showKeywordRowActions && (
                          <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {keywords.map((kw) => {
                        const customerName = kw.customerName ?? kw.Customer?.accountName ?? kw.Customer?.accountShortName ?? kw.customer?.accountName ?? kw.customer?.accountShortName ?? customers.find((c) => c.id === kw.customerId)?.accountName ?? customers.find((c) => c.id === kw.customerId)?.accountShortName ?? '-';
                        return (
                        <tr key={kw.id}>
                          <td className="px-3 py-1.5 text-sm text-gray-900">
                            {customerName}
                          </td>
                          <td className="px-3 py-1.5 text-left text-sm font-medium text-gray-900 align-top">
                            <button
                              type="button"
                              onClick={() => openLogsForKeyword(kw)}
                              className="w-full text-left whitespace-normal break-words text-indigo-600 hover:text-indigo-900 hover:underline"
                              title="View suppression logs for this keyword"
                            >
                              {kw.keyword}
                            </button>
                          </td>
                          <td className="px-3 py-1.5 text-sm text-gray-600">{kw.matchType}</td>
                          <td className="px-1.5 py-1.5 text-sm text-right text-gray-900 tabular-nums font-medium whitespace-nowrap">
                            {kw.suppressedAlertCount ?? 0}
                          </td>
                          <td className="px-3 py-1.5">
                            <span
                              className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded-full ${
                                kw.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {kw.status}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-xs text-gray-700 align-top min-w-0">
                            {kw.createdBy ? (
                              <>
                                <div
                                  className="font-medium text-gray-900 truncate max-w-[14rem]"
                                  title={userDisplayName(kw.createdBy)}
                                >
                                  {userDisplayName(kw.createdBy)}
                                </div>
                                <div className="text-gray-500 tabular-nums whitespace-nowrap">
                                  {new Date(kw.createdAt).toLocaleString()}
                                </div>
                              </>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-xs text-gray-700 align-top min-w-0">
                            {kw.updatedBy ? (
                              <>
                                <div
                                  className="font-medium text-gray-900 truncate max-w-[14rem]"
                                  title={userDisplayName(kw.updatedBy)}
                                >
                                  {userDisplayName(kw.updatedBy)}
                                </div>
                                <div className="text-gray-500 tabular-nums whitespace-nowrap">
                                  {new Date(kw.updatedAt).toLocaleString()}
                                </div>
                              </>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-center text-sm text-gray-700 tabular-nums">
                            <span className="inline-flex items-center justify-center gap-0.5" title="Proof attachments">
                              <PaperClipIcon className="h-4 w-4 text-gray-400 shrink-0" />
                              {kw.attachmentCount ?? 0}
                            </span>
                          </td>
                          {showKeywordRowActions && (
                            <td className="px-3 py-1.5 text-right whitespace-nowrap">
                              {canViewKeyword && (
                                <button
                                  type="button"
                                  onClick={() => openViewModal(kw)}
                                  className="text-gray-600 hover:text-gray-900 mr-3"
                                  title="View details and attachments"
                                  aria-label="View keyword details"
                                >
                                  <EyeIcon className="h-4 w-4 inline" />
                                </button>
                              )}
                              {canEditKeyword && (
                                <button
                                  type="button"
                                  onClick={() => openEditModal(kw)}
                                  className="text-indigo-600 hover:text-indigo-900 mr-3"
                                  title="Edit keyword"
                                  aria-label="Edit keyword"
                                >
                                  <PencilIcon className="h-4 w-4 inline" />
                                </button>
                              )}
                              {canRemoveKeyword && (
                                <button
                                  type="button"
                                  onClick={() => setDeleteTarget(kw)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <TrashIcon className="h-4 w-4 inline" />
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {!keywordsLoading && keywordsTotal > 0 && (
              <div className="flex-shrink-0 px-3 py-2 border-t border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 bg-gray-50/50">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-700">Show</span>
                  <select
                    value={keywordLimit}
                    onChange={(e) => patchSearchParams({ pageSize: e.target.value, page: '1' })}
                    className={`${integrationSelect} py-1.5 text-sm min-w-0`}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-gray-700">per page</span>
                </div>
                <div className="text-sm text-gray-700">
                  Showing{' '}
                  {Math.min((keywordPage - 1) * keywordLimit + 1, keywordsTotal)} to{' '}
                  {Math.min(keywordPage * keywordLimit, keywordsTotal)} of {keywordsTotal}
                </div>
                <div className="flex items-center gap-1 justify-end flex-wrap">
                  <button
                    type="button"
                    onClick={() => patchSearchParams({ page: '1' })}
                    disabled={keywordPage <= 1}
                    className={`${integrationPaginationButton} px-2`}
                  >
                    &laquo;
                  </button>
                  <button
                    type="button"
                    onClick={() => patchSearchParams({ page: String(Math.max(1, keywordPage - 1)) })}
                    disabled={keywordPage <= 1}
                    className={integrationPaginationButton}
                  >
                    Previous
                  </button>
                  <span className="px-2 py-1 text-sm text-gray-700 whitespace-nowrap">
                    Page {keywordsTotalPages > 0 ? keywordPage : 0} of {keywordsTotalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => patchSearchParams({ page: String(Math.min(keywordsTotalPages, keywordPage + 1)) })}
                    disabled={keywordPage >= keywordsTotalPages}
                    className={integrationPaginationButton}
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    onClick={() => patchSearchParams({ page: String(keywordsTotalPages) })}
                    disabled={keywordPage >= keywordsTotalPages || keywordsTotalPages === 0}
                    className={`${integrationPaginationButton} px-2`}
                  >
                    &raquo;
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div
          className="bg-white shadow rounded-lg flex flex-col flex-1 min-h-0 overflow-hidden"
          role="tabpanel"
          id="panel-logs"
          aria-labelledby="tab-logs"
        >
          <div className="flex-shrink-0 px-3 py-2 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search alert, customer, keyword..."
                  value={logsSearchInput}
                  onChange={(e) => setLogsSearchInput(e.target.value)}
                  className={`${integrationSearchInputBlock} !py-1.5 !pr-9 text-sm`}
                />
              </div>
            </div>
            {logFilterChips.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                <ActiveFilterChips
                  chips={logFilterChips}
                  onRemove={handleRemoveLogFilterChip}
                  onClearAll={clearLogFilters}
                />
              </div>
            )}
          </div>
          {logsLoading ? (
            <div className="flex-1 min-h-0 overflow-auto p-4 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/4 mb-3" />
              <div className="space-y-2">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="h-10 bg-gray-200 rounded" />
                ))}
              </div>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex-1 min-h-0 overflow-auto p-6 text-center text-gray-500 text-sm">
              <ClipboardDocumentListIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p>
                {logFilterChips.length > 0 || appliedLogsSearch
                  ? 'No suppression logs match your filters.'
                  : 'No suppression logs yet. Logs are created when an alert matches a keyword during automatic case creation.'}
              </p>
            </div>
          ) : (
            <>
              <div className="flex-1 min-h-0 overflow-auto">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">
                          {renderLogColumnHeader('alert', 'Alert')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">
                          {renderLogColumnHeader('customer', 'Customer')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">
                          {renderLogColumnHeader('matchedKeyword', 'Matched Keyword')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">
                          {renderLogColumnHeader('matchType', 'Match Type')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">
                          Timestamp
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {logs.map((log) => (
                        <tr key={log.id}>
                          <td className="px-3 py-1.5 text-sm">
                            <Link
                              to={`/alerts/${log.Alert.id}`}
                              className="text-indigo-600 hover:text-indigo-900 font-medium"
                            >
                              {log.Alert.number}
                            </Link>
                            <div className="text-xs text-gray-500 truncate max-w-xs leading-tight">{log.Alert.shortDescription}</div>
                          </td>
                          <td className="px-3 py-1.5 text-sm text-gray-600">
                            {log.Customer?.accountName || log.Customer?.accountShortName || '-'}
                          </td>
                          <td className="px-3 py-1.5 text-sm font-medium text-gray-900">{log.matchedKeyword}</td>
                          <td className="px-3 py-1.5 text-sm text-gray-600">{log.matchType}</td>
                          <td className="px-3 py-1.5 text-sm text-gray-600 whitespace-nowrap tabular-nums">
                            {new Date(log.createdAt).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {logsTotal > 0 && (
                <div className="flex-shrink-0 px-3 py-2 border-t border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 bg-gray-50/50">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-gray-700">Show</span>
                    <select
                      value={logLimit}
                      onChange={(e) => patchSearchParams({ logPageSize: e.target.value, logPage: '1' })}
                      className={`${integrationSelect} py-1.5 text-sm min-w-0`}
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-700">per page</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    Showing {Math.min((logPage - 1) * logLimit + 1, logsTotal)} to{' '}
                    {Math.min(logPage * logLimit, logsTotal)} of {logsTotal}
                  </div>
                  <div className="flex items-center gap-1 justify-end flex-wrap">
                    <button
                      type="button"
                      onClick={() => patchSearchParams({ logPage: '1' })}
                      disabled={logPage <= 1}
                      className={`${integrationPaginationButton} px-2`}
                    >
                      &laquo;
                    </button>
                    <button
                      type="button"
                      onClick={() => patchSearchParams({ logPage: String(Math.max(1, logPage - 1)) })}
                      disabled={logPage <= 1}
                      className={integrationPaginationButton}
                    >
                      Previous
                    </button>
                    <span className="px-2 py-1 text-sm text-gray-700 whitespace-nowrap">
                      Page {logsTotalPages > 0 ? logPage : 0} of {logsTotalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => patchSearchParams({ logPage: String(Math.min(logsTotalPages, logPage + 1)) })}
                      disabled={logPage >= logsTotalPages || logsTotalPages === 0}
                      className={integrationPaginationButton}
                    >
                      Next
                    </button>
                    <button
                      type="button"
                      onClick={() => patchSearchParams({ logPage: String(logsTotalPages) })}
                      disabled={logPage >= logsTotalPages || logsTotalPages === 0}
                      className={`${integrationPaginationButton} px-2`}
                    >
                      &raquo;
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* View details (read-only + attachment download) */}
      <Transition show={!!viewingKeyword} as={React.Fragment}>
        <Dialog onClose={closeViewModal} className="relative z-50">
          <Transition.Child
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                <Dialog.Title className="text-lg font-semibold">Keyword details</Dialog.Title>
                {viewDetailMerged && (
                  <div className="mt-4 space-y-4 text-sm">
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                      <div className="min-w-0">
                        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Customer</dt>
                        <dd className="mt-0.5 text-gray-900">
                          {viewDetailMerged.customerName ??
                            viewDetailMerged.Customer?.accountName ??
                            viewDetailMerged.Customer?.accountShortName ??
                            customers.find((c) => c.id === viewDetailMerged.customerId)?.accountName ??
                            customers.find((c) => c.id === viewDetailMerged.customerId)?.accountShortName ??
                            '—'}
                        </dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Keyword</dt>
                        <dd className="mt-0.5 font-medium text-gray-900 break-words">{viewDetailMerged.keyword}</dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Match type</dt>
                        <dd className="mt-0.5 text-gray-900">{matchTypeLabel(viewDetailMerged.matchType)}</dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</dt>
                        <dd className="mt-0.5">
                          <span
                            className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded-full ${
                              viewDetailMerged.status === 'ACTIVE'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {viewDetailMerged.status}
                          </span>
                        </dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Suppressed alerts</dt>
                        <dd className="mt-0.5 text-gray-900 tabular-nums">
                          {viewDetailMerged.suppressedAlertCount ?? 0}
                        </dd>
                      </div>
                    </dl>

                    <div className="rounded-md bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-600 space-y-1">
                      {viewDetailMerged.createdBy && (
                        <p>
                          <span className="font-medium text-gray-700">Added:</span>{' '}
                          {userDisplayName(viewDetailMerged.createdBy)} ·{' '}
                          {new Date(viewDetailMerged.createdAt).toLocaleString()}
                        </p>
                      )}
                      {viewDetailMerged.updatedBy &&
                        new Date(viewDetailMerged.updatedAt).getTime() !==
                          new Date(viewDetailMerged.createdAt).getTime() && (
                          <p>
                            <span className="font-medium text-gray-700">Last updated:</span>{' '}
                            {userDisplayName(viewDetailMerged.updatedBy)} ·{' '}
                            {new Date(viewDetailMerged.updatedAt).toLocaleString()}
                          </p>
                        )}
                    </div>

                    <div className="border-t border-gray-100 pt-4">
                      <h3 className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                        <PaperClipIcon className="h-4 w-4 text-gray-500" />
                        Proof attachments
                      </h3>
                      {keywordDetailLoading && viewingKeyword?.id === keywordDetailId ? (
                        <p className="mt-2 text-xs text-gray-500">Loading attachments…</p>
                      ) : viewingKeyword &&
                        keywordDetailData?.id === viewingKeyword.id &&
                        detailAttachments.length === 0 ? (
                        <p className="mt-2 text-xs text-gray-500">No proof files uploaded.</p>
                      ) : viewingKeyword && keywordDetailData?.id === viewingKeyword.id ? (
                        <ul className="mt-2 space-y-4">
                          {detailAttachments.map((att) => (
                            <ViewModalAttachmentRow key={att.id} keywordId={viewingKeyword.id} att={att} />
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-2 text-xs text-gray-500">Loading…</p>
                      )}
                    </div>

                    <div className="flex justify-end pt-2">
                      <button type="button" onClick={closeViewModal} className={integrationSecondaryButton}>
                        Close
                      </button>
                      {canEditKeyword && viewingKeyword && (
                        <button
                          type="button"
                          onClick={() => {
                            const kw = viewingKeyword;
                            closeViewModal();
                            openEditModal(kw);
                          }}
                          className={`${integrationToolbarPrimaryButton} ml-2`}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Add/Edit Modal */}
      <Transition show={showModal} as={React.Fragment}>
        <Dialog onClose={closeModal} className="relative z-50">
          <Transition.Child
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
                <Dialog.Title className="text-lg font-semibold">
                  {editingKeyword ? 'Edit Keyword' : 'Add Keyword'}
                </Dialog.Title>
                {keywordModalDetail && (
                  <div className="mt-3 rounded-md bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-600 space-y-1">
                    {keywordModalDetail.createdBy && (
                      <p>
                        <span className="font-medium text-gray-700">Added:</span>{' '}
                        {userDisplayName(keywordModalDetail.createdBy)} ·{' '}
                        {new Date(keywordModalDetail.createdAt).toLocaleString()}
                      </p>
                    )}
                    {keywordModalUpdatedDiffers && keywordModalDetail.updatedBy && (
                      <p>
                        <span className="font-medium text-gray-700">Last updated:</span>{' '}
                        {userDisplayName(keywordModalDetail.updatedBy)} ·{' '}
                        {new Date(keywordModalDetail.updatedAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
                <form onSubmit={handleSave} className="mt-4 space-y-4">
                  {!editingKeyword && (
                    <div>
                      <label htmlFor="case-suppression-keyword-customer" className="block text-sm font-medium text-gray-700">
                        Customer *
                      </label>
                      <CustomerSelectCombobox
                        inputId="case-suppression-keyword-customer"
                        customers={customers}
                        value={formData.customerId}
                        onChange={(id) => setFormData((f) => ({ ...f, customerId: id }))}
                        required={!editingKeyword}
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Keyword *</label>
                    <input
                      type="text"
                      value={formData.keyword}
                      onChange={(e) => setFormData((f) => ({ ...f, keyword: e.target.value }))}
                      className={integrationModalField}
                      placeholder="e.g. disk, error"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Match Type</label>
                    <select
                      value={formData.matchType}
                      onChange={(e) => setFormData((f) => ({ ...f, matchType: e.target.value as 'EXACT' | 'CONTAINS' }))}
                      className={integrationModalField}
                    >
                      {MATCH_TYPES.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Exact: whole word only. Contains: anywhere in alert METADATA.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData((f) => ({ ...f, status: e.target.value as 'ACTIVE' | 'INACTIVE' }))}
                      className={integrationModalField}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="border-t border-gray-100 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Proof attachments (optional)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Email (.eml, .msg), images, PDF, Word, or .txt — evidence from the client for this suppression.
                    </p>
                    {editingKeyword && (
                      <div className="mb-3">
                        {keywordDetailLoading ? (
                          <p className="text-xs text-gray-500">Loading attachments…</p>
                        ) : detailAttachments.length === 0 ? (
                          <p className="text-xs text-gray-500">No files uploaded yet.</p>
                        ) : (
                          <ul className="space-y-2">
                            {detailAttachments.map((att) => (
                              <li
                                key={att.id}
                                className="flex items-start justify-between gap-2 rounded border border-gray-100 bg-gray-50/80 px-2 py-1.5 text-xs"
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium text-gray-900 truncate" title={att.originalName}>
                                    {att.originalName}
                                  </div>
                                  <div className="text-gray-500">
                                    {att.uploadedBy ? userDisplayName(att.uploadedBy) : '—'} ·{' '}
                                    {new Date(att.createdAt).toLocaleString()}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => downloadKeywordAttachment(editingKeyword.id, att)}
                                    className="p-1 text-indigo-600 hover:text-indigo-800"
                                    title="Download"
                                  >
                                    <ArrowDownTrayIcon className="h-4 w-4" />
                                  </button>
                                  {canEditKeyword && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            `Remove attachment "${att.originalName}"?`
                                          )
                                        ) {
                                          deleteAttachment.mutate({
                                            keywordId: editingKeyword.id,
                                            attachmentId: att.id,
                                          });
                                        }
                                      }}
                                      className="p-1 text-red-600 hover:text-red-800"
                                      title="Remove"
                                      disabled={deleteAttachment.isPending}
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                    <input
                      type="file"
                      multiple
                      accept={PROOF_ACCEPT}
                      className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setPendingProofFiles((prev) => [...prev, ...files]);
                        e.target.value = '';
                      }}
                    />
                    {pendingProofFiles.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {pendingProofFiles.map((f, idx) => (
                          <li
                            key={`${f.name}-${idx}`}
                            className="flex items-center justify-between text-xs text-gray-700 bg-indigo-50/50 rounded px-2 py-1"
                          >
                            <span className="truncate">{f.name}</span>
                            <button
                              type="button"
                              className="text-red-600 hover:underline shrink-0 ml-2"
                              onClick={() =>
                                setPendingProofFiles((prev) => prev.filter((_, i) => i !== idx))
                              }
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className={integrationSecondaryButton}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={
                        saving ||
                        (editingKeyword ? !canEditKeyword : !canAddKeyword)
                      }
                      className={integrationToolbarPrimaryButton}
                    >
                      {saving ? 'Saving...' : editingKeyword ? 'Update' : 'Add'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Delete Confirmation Modal */}
      <Transition show={!!deleteTarget} as={React.Fragment}>
        <Dialog onClose={() => setDeleteTarget(null)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
              <Dialog.Title className="text-lg font-semibold">Delete Keyword</Dialog.Title>
              <p className="mt-2 text-sm text-gray-600">
                Are you sure you want to delete the keyword &quot;{deleteTarget?.keyword}&quot;?
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeleteTarget(null)}
                  className={integrationSecondaryButton}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => deleteTarget && handleDelete(deleteTarget)}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default CaseSuppressionKeywords;
